import { getCookie, setCookie } from "~/utils/cookie";
import { request } from "~/utils/api";
import qs from "querystringify";

export const VERIFICATION_COOKIE_NAME = "x-cft-verified";

export const SEARCH_ENGINE_CHECK_TOKEN = "SEARCH_ENGINE_CHECK";

/**
 * 检测是否是搜索引擎爬虫（仅基于 User-Agent）
 * 实际验证由后端通过 IP 反向 DNS 完成
 */
const isSearchEngineCrawler = (req) => {
  if (!req || !req.headers) {
    return false;
  }

  const userAgent = req.headers["user-agent"] || "";

  const crawlerPatterns = [
    /applebot/i,
    /googlebot/i,
    /bingbot/i,
    /slurp/i, // Yahoo
    /duckduckbot/i,
    /baiduspider/i,
    /yandexbot/i,
    /sogou/i,
    /360spider/i,
    /yisouspider/i,
    /twitterbot/i,
    /facebookexternalhit/i,
  ];

  return crawlerPatterns.some((pattern) => pattern.test(userAgent));
};

export const setVerificationCookie = (token, expiresAt) => {
  const expiresDate = expiresAt
    ? new Date(expiresAt * 1000)
    : new Date(Date.now() + 2 * 60 * 60 * 1000);
  return setCookie(VERIFICATION_COOKIE_NAME, token, {
    expires: expiresDate,
    path: "/",
  });
};

/**
 * 检查验证状态
 * @param {object} req - 请求对象（服务端）
 * @returns {Promise<{isValid: boolean, token: string|null}>}
 */
export const checkVerification = async (req) => {
  const isSearchEngine = isSearchEngineCrawler(req);
  const userAgent = (req?.headers || {})["user-agent"] || "";
  console.log("isSearchEngine && userAgent", isSearchEngine, userAgent);
  const verificationToken =
    getCookie(VERIFICATION_COOKIE_NAME, req) ||
    (isSearchEngine ? SEARCH_ENGINE_CHECK_TOKEN : null);

  if (!verificationToken && !isSearchEngine) {
    return { isValid: false, token: null };
  }

  try {
    // 构建请求头
    const headers = {
      "Content-Type": "application/json",
      "X-CFT-TOKEN": verificationToken,
    };

    // 在 SSR 环境下，获取用户的真实 IP/UA 并传递给后端
    if (req) {
      const realIp = getRealIp(req);
      if (realIp) {
        headers["X-Forwarded-For"] = realIp;
        headers["X-Real-IP"] = realIp;
        headers["X-Original-User-Agent"] = userAgent;
      }
    }
    // 调用后端 API 验证 token 是否有效
    const res = await request({
      url: "/turnstile/check",
      method: "POST",
      headers: headers,
      data: {
        token: verificationToken,
        is_search_engine: isSearchEngine,
      },
    });

    if (res.data.verified && res.data.token) {
      setVerificationCookie(res.data.token, res.data.expires_at);
    }
    // Token 有效（200 响应）
    return { isValid: true, token: verificationToken };
  } catch (err) {
    // Token 验证失败（422）或其他错误
    return { isValid: false, token: verificationToken };
  }
};

/**
 * 构建完整的当前页面 URL（包含所有 query 参数）
 * @param {string} pathname - 页面路径（如 "/jobs-channel"）
 * @param {object} query - query 参数对象
 * @returns {string} 完整的 URL
 */
export const buildFullUrl = (pathname, query) => {
  const queryString = qs.stringify(query);
  return queryString ? `${pathname}?${queryString}` : pathname;
};

/**
 * 构建验证页面的跳转 URL
 * @param {string} redirectUrl - 验证成功后的回跳 URL（可选）
 * @returns {string} 验证页面的 URL
 */
export const buildVerificationRedirect = (redirectUrl) => {
  if (redirectUrl) {
    const queryString = qs.stringify({ redirect: redirectUrl });
    return `/verification?${queryString}`;
  }
  return "/verification";
};

/**
 * 清除验证 cookie（客户端）
 */
export const clearVerificationCookie = () => {
  if (typeof document !== "undefined") {
    document.cookie = `${VERIFICATION_COOKIE_NAME}=; path=/; max-age=0`;
  }
};

/**
 * 在服务端响应中清除验证 cookie
 * @param {object} res - Next.js 响应对象
 */
export const clearVerificationCookieInResponse = (res) => {
  if (res) {
    // 设置 cookie 过期时间为过去的时间，从而清除它
    res.setHeader(
      "Set-Cookie",
      `${VERIFICATION_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
    );
  }
};

/**
 * 从请求对象中获取用户真实 IP
 * @param {object} req - Next.js 请求对象
 * @returns {string|null} 用户的真实 IP
 */
const getRealIp = (req) => {
  if (!req || !req.headers) {
    return null;
  }

  // 按优先级依次尝试获取真实 IP

  console.log("x-real-ip", req.headers["x-real-ip"]);
  console.log("x-forwarded-for", req.headers["x-forwarded-for"]);

  // 1. X-Real-IP（Nginx 等反向代理传递的真实 IP）
  const realIp = req.headers["x-real-ip"];
  if (realIp) {
    return realIp;
  }

  // 2. X-Forwarded-For（代理服务器传递的真实 IP，可能包含多个 IP，取最后一个）
  const forwardedFor = req.headers["x-forwarded-for"];
  if (forwardedFor) {
    const ips = forwardedFor.split(",").map((ip) => ip.trim());
    return ips[ips.length - 1];
  }

  // 3. CF-Connecting-IP（Cloudflare 传递的真实 IP）
  const cfConnectingIp = req.headers["cf-connecting-ip"];
  if (cfConnectingIp) {
    return cfConnectingIp;
  }

  // 4. 直接连接的 IP（没有代理时）
  const remoteAddress =
    req.socket?.remoteAddress || req.connection?.remoteAddress;
  if (remoteAddress) {
    return remoteAddress;
  }

  return null;
};
